import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssetUploadOptions,
  AudioKind,
  UserAssetConstraints,
  getAssetValidationMessage,
} from '../services/projectAssets';
import { actionFailureMessage } from '../utils/actionFailure';
import {
  fileExtension,
  formatAudioLimit,
  formatClockTime,
  isAudioFile,
  isFontFile,
  isUploadTooLarge,
  preselectAudioKind,
} from '../utils/assetFiles';
import { measureAudioDuration } from '../utils/audioDuration';
import InlineError from './InlineError';

const MEASURE_TIMEOUT_MS = 5000;
const BYTES_PER_MB = 1024 * 1024;
const AUDIO_KINDS: AudioKind[] = ['SoundEffect', 'Music'];

export interface AssetUpload {
  name: string;
  description: string;
  file: File;
  options: AssetUploadOptions;
}

interface AssetUploadFormProps {
  constraints: UserAssetConstraints | null;
  existingNames: string[];
  onUpload: (upload: AssetUpload) => Promise<void>;
  onCancel: () => void;
}

export default function AssetUploadForm({ constraints, existingNames, onUpload, onCancel }: AssetUploadFormProps) {
  const { t } = useTranslation(['common']);
  const kindGroupName = useId();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [license, setLicense] = useState('');
  const [audioKind, setAudioKind] = useState<AudioKind | null>(null);
  const [loopable, setLoopable] = useState(false);
  const [measuredSeconds, setMeasuredSeconds] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const measuringFile = useRef<File | null>(null);
  const kindChosenByUser = useRef(false);
  const fileIsAudio = !!file && isAudioFile(file, constraints);
  const fileIsFont = !!file && isFontFile(file);

  useEffect(
    () => () => {
      measuringFile.current = null;
    },
    [],
  );

  const audioKindLabel = (kind: AudioKind) =>
    kind === 'Music' ? t('assets.audio.music', 'Music') : t('assets.audio.soundEffect', 'Sound effect');

  const chooseFile = (chosen: File | null) => {
    setFile(chosen);
    setMeasuredSeconds(null);
    measuringFile.current = chosen;
    if (!kindChosenByUser.current) setAudioKind(null);
    if (chosen && !name) {
      const baseName = chosen.name.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9_-]/g, '-');
      setName(constraints ? baseName.slice(0, constraints.maxNameLength) : baseName);
    }
    if (!chosen || !constraints || !isAudioFile(chosen, constraints)) return;
    measureAudioDuration(chosen, MEASURE_TIMEOUT_MS).then((seconds) => {
      if (measuringFile.current !== chosen) return;
      setMeasuredSeconds(seconds);
      if (!kindChosenByUser.current) setAudioKind(preselectAudioKind(seconds, constraints));
    });
  };

  const chooseKind = (kind: AudioKind) => {
    kindChosenByUser.current = true;
    setAudioKind(kind);
  };

  const sizeCapMessage = (limits: UserAssetConstraints) =>
    fileIsAudio
      ? t(
          'assets.audio.tooLarge',
          'Max audio file size is {{size}}MB. For long music, upload an MP3 or OGG rather than WAV or FLAC.',
          { size: Math.floor(limits.audio.maxSourceBytes / BYTES_PER_MB) },
        )
      : t('assets.tooLarge', 'Max file size is {{size}}MB.', {
          size: Math.floor(limits.maxFileSizeBytes / BYTES_PER_MB),
        });

  const validate = (): string | null => {
    if (!file) return t('assets.fileRequired', 'Choose a file.');
    if (!constraints) return null;
    if (!constraints.allowedFileExtensions.includes(fileExtension(file.name))) {
      return t('assets.badType', 'Allowed types: {{types}}', {
        types: constraints.allowedFileExtensions.join(', '),
      });
    }
    const maxBytes = fileIsAudio ? constraints.audio.maxSourceBytes : constraints.maxFileSizeBytes;
    if (file.size > maxBytes) return sizeCapMessage(constraints);
    if (!new RegExp(constraints.namePattern).test(name)) {
      return t(
        'assets.badName',
        "Name must be 1-{{max}} characters: letters, digits, '-' or '_' (no spaces).",
        { max: constraints.maxNameLength },
      );
    }
    if (existingNames.some((existing) => existing.toLowerCase() === name.toLowerCase())) {
      return t('assets.nameTaken', 'An asset with this name is already in this project.');
    }
    if (!description.trim()) {
      return t('assets.descriptionRequired', 'Describe what this asset is for.');
    }
    if (existingNames.length >= constraints.maxAssetsPerProject) {
      return t('assets.limitReached', 'A project can have at most {{max}} assets.', {
        max: constraints.maxAssetsPerProject,
      });
    }
    if (fileIsAudio) {
      if (!audioKind) {
        return t('assets.audio.kindRequired', 'Choose whether this is a sound effect or music.');
      }
      const { minMusicSeconds, maxMusicSeconds } = constraints.audio;
      if (
        audioKind === 'Music' &&
        measuredSeconds !== null &&
        (measuredSeconds < minMusicSeconds || measuredSeconds > maxMusicSeconds)
      ) {
        return t('assets.audio.musicLength', 'Music must be {{min}} to {{max}} long; this file is {{length}}.', {
          min: formatAudioLimit(minMusicSeconds),
          max: formatAudioLimit(maxMusicSeconds),
          length: formatClockTime(measuredSeconds),
        });
      }
    }
    return null;
  };

  const uploadFailureMessage = (e: unknown): string => {
    if (isUploadTooLarge(e) && constraints) return sizeCapMessage(constraints);
    return actionFailureMessage(
      e,
      getAssetValidationMessage(e) ?? t('assets.uploadFailed', 'Upload failed. Please try again.'),
    );
  };

  const handleUpload = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const trimmedLicense = license.trim();
    const options: AssetUploadOptions = fileIsAudio
      ? { audioKind: audioKind!, loopable }
      : fileIsFont && trimmedLicense
        ? { licenseText: trimmedLicense }
        : {};
    setIsUploading(true);
    setError(null);
    try {
      await onUpload({ name, description: description.trim(), file: file!, options });
    } catch (e) {
      setError(uploadFailureMessage(e));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--line-2)',
        borderRadius: 10,
        background: 'var(--surface)',
      }}
    >
      <input
        type="file"
        aria-label={t('assets.file', 'Asset file')}
        accept={constraints?.allowedFileExtensions.join(',')}
        onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
      />
      <input
        type="text"
        className="input"
        placeholder={t('assets.namePlaceholder', 'Unique name (e.g. hero-sprite)')}
        value={name}
        maxLength={constraints?.maxNameLength}
        onChange={(e) => setName(e.target.value)}
      />
      <textarea
        className="textarea"
        style={{ minHeight: 60 }}
        placeholder={t('assets.descriptionPlaceholder', 'What is this asset and what should it be used for?')}
        value={description}
        maxLength={constraints?.maxDescriptionLength}
        onChange={(e) => setDescription(e.target.value)}
      />
      {fileIsFont && (
        <textarea
          className="textarea"
          style={{ minHeight: 60 }}
          placeholder={t(
            'assets.licensePlaceholder',
            'Font license text (optional) — paste it to ship it alongside the font in your built game.',
          )}
          value={license}
          onChange={(e) => setLicense(e.target.value)}
        />
      )}
      {fileIsAudio && constraints && (
        <>
          <div
            role="radiogroup"
            aria-label={t('assets.audio.kind', 'Kind of sound')}
            style={{ display: 'flex', gap: 16, fontSize: 13 }}
          >
            {AUDIO_KINDS.map((kind) => (
              <label key={kind} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={kindGroupName}
                  value={kind}
                  checked={audioKind === kind}
                  onChange={() => chooseKind(kind)}
                />
                {audioKindLabel(kind)}
              </label>
            ))}
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
            {t(
              'assets.audio.hint',
              'Sound effects up to {{maxSoundEffect}}, music {{minMusic}} to {{maxMusic}}. {{types}} up to {{size}}MB.',
              {
                maxSoundEffect: formatAudioLimit(constraints.audio.maxSoundEffectSeconds),
                minMusic: formatAudioLimit(constraints.audio.minMusicSeconds),
                maxMusic: formatAudioLimit(constraints.audio.maxMusicSeconds),
                types: constraints.audio.sourceFileExtensions
                  .map((extension) => extension.replace('.', '').toUpperCase())
                  .join(', '),
                size: Math.floor(constraints.audio.maxSourceBytes / BYTES_PER_MB),
              },
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={loopable} onChange={(e) => setLoopable(e.target.checked)} />
            {t('assets.audio.loopable', 'Loops seamlessly')}
          </label>
          <div className="muted" style={{ fontSize: 11 }}>
            {t('assets.audio.rights', 'Upload only audio you have the right to use in your game.')}
          </div>
        </>
      )}
      {error && <InlineError>{error}</InlineError>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn sm" onClick={onCancel} disabled={isUploading}>
          {t('assets.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          className="btn primary sm"
          onClick={handleUpload}
          disabled={isUploading || !file}
        >
          {isUploading
            ? fileIsAudio
              ? t('assets.audio.uploading', 'Uploading and converting…')
              : t('assets.uploading', 'Uploading…')
            : t('assets.upload', 'Upload')}
        </button>
      </div>
    </div>
  );
}
